// OnboardingSteps · "From signup to first trade in under an hour".
// Three mockup cards — each shows a miniature preview of what the
// step actually looks like:
//   1. Signup form auto-filling field by field
//   2. KYC ID-card with a sweeping scan bar + green checkmark
//   3. Live mini candle chart painting in real time
// Cards auto-cycle through 1 → 2 → 3 (one is "active" with a brand
// halo). Hovering pauses the cycle and locks to that step. Vertical
// 3-column grid on desktop, stacked on mobile.
// Designed to be visually distinct from LiquidityEcosystem's
// horizontal flow concept.
import { useEffect, useState, useRef, useMemo } from "react";
import { motion, useInView } from "framer-motion";
import {
  UserPlus, ShieldCheck, LineChart, Check, ChevronRight, ArrowRight,
  Mail, Lock, Globe, IdCard, ScanLine, Timer, Sparkles, Zap,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { useCtaTarget } from "@/lib/useCtaTarget";

const STEPS_ICONS = [UserPlus, ShieldCheck, LineChart];
const STEPS_CTA_HREFS = [null, "/safety-of-funds", "/platforms/mt5"];
const STEPS_CTA_TARGETS = ["_blank", undefined, undefined];

const STEPS_META = [
  { id: 1, Icon: UserPlus,    cta: { href: null, target: "_blank" } },
  { id: 2, Icon: ShieldCheck, cta: { href: "/safety-of-funds" } },
  { id: 3, Icon: LineChart,   cta: { href: "/platforms/mt5" } },
];

const CYCLE_MS = 4200;

/* ============== Mockup 1 · signup form fills in ============== */
function FormMockup({ active }) {
  const { t } = useTranslation();
  return (
    <div className="relative w-full h-[220px] rounded-lg overflow-hidden p-4 flex flex-col gap-2.5"
      style={{ background: "var(--surface-2)", border: "1px solid var(--line-strong)" }}>
      <div className="flex items-center gap-1.5 mb-1.5">
        <span className="w-2 h-2 rounded-full" style={{ background: "#f06a6a" }} />
        <span className="w-2 h-2 rounded-full" style={{ background: "#f5b14a" }} />
        <span className="w-2 h-2 rounded-full" style={{ background: "#54d97c" }} />
      </div>
      <FieldRow Icon={Mail}  width="78%" delay={0.2} active={active} />
      <FieldRow Icon={Lock}  width="62%" delay={0.6} active={active} />
      <FieldRow Icon={Globe} width="45%" delay={1.0} active={active} />
      {/* Submit button · taller, with a leading sparkle so it visually
          balances the chart card on the right (also h-12). The pulsing
          shadow draws the eye while the chart is "Connecting". */}
      <motion.div
        className="mt-auto inline-flex items-center justify-center gap-2 h-12 rounded-lg text-[12px] font-mono font-semibold uppercase tracking-[0.18em]"
        style={{
          background: "linear-gradient(135deg, var(--brand) 0%, var(--brand-deep) 100%)",
          color: "#ffffff",
          boxShadow: "inset 0 1px 0 rgba(255,255,255,0.25)",
        }}
        animate={active ? { boxShadow: [
          "inset 0 1px 0 rgba(255,255,255,0.25), 0 0 0 0 color-mix(in srgb, var(--brand) 50%, transparent)",
          "inset 0 1px 0 rgba(255,255,255,0.25), 0 0 0 10px color-mix(in srgb, var(--brand) 0%, transparent)",
        ] } : {}}
        transition={{ duration: 1.6, repeat: active ? Infinity : 0 }}
      >
        {t("common.createAccount")} <Check size={14} strokeWidth={2.6} />
      </motion.div>
    </div>
  );
}
function FieldRow({ Icon, width, delay, active }) {
  return (
    <div className="flex items-center gap-2 h-8 rounded-md px-2.5"
      style={{ background: "var(--surface)", border: "1px solid var(--line)" }}>
      <Icon size={12} className="text-text-3 shrink-0" />
      <div className="relative flex-1 h-1.5 overflow-hidden">
        <motion.div
          className="absolute inset-y-0 left-0 rounded-full"
          style={{ background: "color-mix(in srgb, var(--brand) 70%, transparent)" }}
          initial={{ width: 0 }}
          animate={active ? { width } : { width: "12%" }}
          transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
        />
      </div>
    </div>
  );
}

/* ============== Mockup 2 · ID-card scan + green check ============== */
function KYCMockup({ active }) {
  const { t } = useTranslation();
  return (
    <div className="relative w-full h-[220px] rounded-lg overflow-hidden flex items-center justify-center"
      style={{ background: "var(--surface-2)", border: "1px solid var(--line-strong)" }}>
      {/* ID card · scaled up to fill the larger mockup area */}
      <motion.div
        className="relative w-[180px] h-[100px] rounded-md flex p-2.5"
        style={{
          background: "linear-gradient(135deg, color-mix(in srgb, var(--brand) 25%, var(--surface)) 0%, var(--surface-3) 100%)",
          border: "1px solid color-mix(in srgb, var(--brand) 30%, transparent)",
          boxShadow: "0 16px 40px -16px color-mix(in srgb, var(--brand) 60%, transparent)",
        }}
        animate={active ? { rotate: [-1.5, 1.5, -1.5], y: [0, -4, 0] } : { rotate: 0 }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
      >
        <div className="flex flex-col gap-1">
          <div className="w-12 h-16 rounded-sm grid place-items-center" style={{ background: "var(--surface)" }}>
            <IdCard size={24} className="text-text-3" />
          </div>
        </div>
        <div className="flex flex-col gap-1.5 ml-2.5 mt-1 flex-1">
          <div className="h-1.5 w-3/4 rounded-full" style={{ background: "color-mix(in srgb, var(--brand) 55%, transparent)" }} />
          <div className="h-1.5 w-1/2 rounded-full" style={{ background: "color-mix(in srgb, var(--text-3) 40%, transparent)" }} />
          <div className="h-1.5 w-2/3 rounded-full" style={{ background: "color-mix(in srgb, var(--text-3) 40%, transparent)" }} />
          <div className="h-1.5 w-1/3 rounded-full mt-auto" style={{ background: "color-mix(in srgb, var(--text-3) 40%, transparent)" }} />
        </div>

        {/* Sweeping scan bar */}
        <motion.div
          className="absolute inset-y-1.5 w-[2px] rounded-full pointer-events-none"
          style={{
            background: "linear-gradient(180deg, transparent, var(--brand), transparent)",
            boxShadow: "0 0 16px color-mix(in srgb, var(--brand) 95%, transparent)",
          }}
          animate={active ? { left: ["6%", "94%", "6%"] } : { left: "6%" }}
          transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
        />
      </motion.div>

      {/* Verified pill · sits in the bottom-right corner */}
      <motion.div
        className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] font-mono uppercase tracking-[0.18em]"
        style={{ background: "rgba(80,220,130,0.16)", color: "#54d97c", border: "1px solid rgba(80,220,130,0.4)" }}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={active ? { opacity: 1, scale: 1 } : { opacity: 0.55, scale: 1 }}
        transition={{ duration: 0.5, delay: 1.6 }}
      >
        <Check size={11} strokeWidth={3} /> {t("common.verified")}
      </motion.div>

      {/* Scanning icon corner */}
      <div className="absolute top-3 left-3 text-text-3">
        <ScanLine size={14} />
      </div>
    </div>
  );
}

/* ============== Mockup 3 · live mini candle chart ============== */
// Pulls the SAME real candles the MT5 showcase uses · backend
// /api/market/history?symbol=EURUSD&timeframe=5m. Last candle gets
// the live tick from /api/market/quotes merged in so it visibly
// paints OHLC in real time. NO synthetic waveform fallback · we show
// a skeleton if the data isn't ready yet.
function ChartMockup({ active }) {
  const { t } = useTranslation();
  const [bars, setBars] = useState([]);
  const [liveBid, setLiveBid] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let timer = null;
    // Retry-poll the history endpoint until we have ≥ 4 bars (the
    // backend cache can be empty on first hit while the SignalR hub
    // warms up). Refresh every 30 s once we have enough.
    const fetchOnce = () => {
      api.get("/market/history?symbol=EURUSD&timeframe=1m")
        .then(({ data }) => {
          if (cancelled) return;
          const newBars = Array.isArray(data?.bars) ? data.bars : [];
          // Sticky last-known-good · don't flicker back to skeleton if
          // the backend cache temporarily empties.
          setBars((prev) => (newBars.length >= 4 ? newBars : prev));
          const delay = newBars.length >= 4 ? 30000 : 1200;
          timer = setTimeout(fetchOnce, delay);
        })
        .catch(() => {
          if (cancelled) return;
          timer = setTimeout(fetchOnce, 2000);
        });
    };
    fetchOnce();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, []);

  // Poll the live quote · gives the last candle a real-time close.
  useEffect(() => {
    let cancelled = false;
    const pull = () =>
      api.get("/market/quotes?symbols=EURUSD")
        .then(({ data }) => {
          if (cancelled) return;
          const q = (data?.quotes || []).find((x) => x.symbol === "EURUSD");
          if (q && Number.isFinite(q.bid)) setLiveBid(q.bid);
        })
        .catch(() => {});
    pull();
    // Was 4000ms · slowed to 8000ms so the homepage doesn't re-fetch the
    // recent-activity feed every 4 seconds (eats main thread on slower devices).
    const id = setInterval(pull, 8000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const N = 18;
  const hasReal = bars.length >= 4;
  const W = 220, H = 200;

  // Loading skeleton · placeholder ghost candles + reconnecting label.
  // Renders a fully populated row of grey pulsing bars so the card
  // never looks empty / warped, even when the live feed is down.
  if (!hasReal) {
    const ghostN = 18;
    const Wg = 220, Hg = 195;
    const slotG = (Wg - 6) / ghostN;
    return (
    <div className="relative w-full h-[220px] rounded-lg overflow-hidden p-2 flex flex-col"
      style={{ background: "var(--surface-2)", border: "1px solid var(--line-strong)" }}>
        <div className="flex items-center justify-between text-[9.5px] font-mono uppercase tracking-[0.18em] text-text-3 px-1 mb-1">
          <span>EURUSD · M1</span>
          <span className="inline-flex items-center gap-1 text-text-3">
            <motion.span
              className="w-1.5 h-1.5 rounded-full"
              style={{ background: "color-mix(in srgb, var(--brand) 70%, transparent)" }}
              animate={{ opacity: [0.3, 1, 0.3] }}
              transition={{ duration: 1.4, repeat: Infinity }}
            />
            {t("ui.onboarding.chartConnecting")}
          </span>
        </div>
        <svg viewBox={`0 0 ${Wg} ${Hg}`} preserveAspectRatio="none" className="w-full flex-1">
          {Array.from({ length: ghostN }, (_, i) => {
            const cx = 3 + slotG * (i + 0.5);
            // Deterministic placeholder pattern (no random jiggle)
            const h1 = 42 + ((i * 11) % 60);
            const yT = (Hg - h1) / 2;
            return (
              <motion.g key={i}
                animate={{ opacity: [0.16, 0.4, 0.16] }}
                transition={{ duration: 1.8, delay: (i % 6) * 0.12, repeat: Infinity, ease: "easeInOut" }}
              >
                <line x1={cx} x2={cx} y1={yT - 5} y2={yT + h1 + 5} stroke="color-mix(in srgb, var(--text-3) 55%, transparent)" strokeWidth="0.5" />
                <rect x={cx - 1.4} y={yT} width="2.8" height={h1} rx="0.5" fill="color-mix(in srgb, var(--text-3) 40%, transparent)" />
              </motion.g>
            );
          })}
        </svg>
      </div>
    );
  }

  // Build the candle array from REAL bars
  const slice = bars.slice(-N).map((b) => ({ o: b.o, h: b.h, l: b.l, c: b.c }));
  // Merge live bid into the last candle for real-time painting
  if (active && Number.isFinite(liveBid) && slice.length) {
    const last = { ...slice[slice.length - 1] };
    last.c = liveBid;
    last.h = Math.max(last.h, liveBid);
    last.l = Math.min(last.l, liveBid);
    slice[slice.length - 1] = last;
  }

  const allVals = slice.flatMap((c) => [c.h, c.l]);
  const rawMin = Math.min(...allVals);
  const rawMax = Math.max(...allVals);
  const rawSpan = rawMax - rawMin || rawMax * 0.0008 || 1;
  const pad = rawSpan * 0.18;
  const min = rawMin - pad;
  const max = rawMax + pad;
  const span = max - min;

  const slot = (W - 6) / slice.length;
  const bodyW = slot * 0.7;
  const yOf = (v) => H - ((v - min) / span) * (H - 14) - 4;
  const last = slice[slice.length - 1];
  const liveUp = last.c >= last.o;

  return (
    <div className="relative w-full h-[220px] rounded-lg overflow-hidden p-2"
      style={{ background: "var(--surface-2)", border: "1px solid var(--line-strong)" }}>
      <div className="flex items-center justify-between text-[9.5px] font-mono uppercase tracking-[0.18em] text-text-3 px-1 mb-1">
        <span>EURUSD · M1</span>
        <motion.span
          className="inline-flex items-center gap-1 text-[#54d97c]"
          animate={{ opacity: [1, 0.5, 1] }}
          transition={{ duration: 1.4, repeat: Infinity }}
        >
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#54d97c" }} />
          LIVE
        </motion.span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-[80px]">
        {slice.map((c, i) => {
          const cx = 3 + slot * (i + 0.5);
          const up = c.c >= c.o;
          const col = up ? "#54d97c" : "#f06a6a";
          const yTop = yOf(Math.max(c.o, c.c));
          const yBot = yOf(Math.min(c.o, c.c));
          const yHigh = yOf(c.h);
          const yLow = yOf(c.l);
          const bodyH = Math.max(1, yBot - yTop);
          const isLive = i === slice.length - 1;
          return (
            <g key={i}>
              <line x1={cx} x2={cx} y1={yHigh} y2={yLow} stroke={col} strokeWidth="0.8" strokeOpacity="0.9" />
              {isLive ? (
                <motion.rect
                  x={cx - bodyW / 2}
                  width={bodyW}
                  rx={0.5}
                  fill={up ? col : "color-mix(in srgb, " + col + " 70%, transparent)"}
                  stroke={col}
                  strokeWidth="0.4"
                  animate={{ y: yTop, height: bodyH }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                />
              ) : (
                <rect
                  x={cx - bodyW / 2}
                  y={yTop}
                  width={bodyW}
                  height={bodyH}
                  rx={0.5}
                  fill={up ? col : "color-mix(in srgb, " + col + " 70%, transparent)"}
                  stroke={col}
                  strokeWidth="0.4"
                />
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

const MOCKUP_BY_STEP = [FormMockup, KYCMockup, ChartMockup];

/* ============== Main section ============== */
function StepCard({ step, index, active, onEnter, onLeave, ctaHrefOverride, ctaTargetOverride, ctaRelOverride }) {
  const { Icon } = step;
  const Mockup = MOCKUP_BY_STEP[index];
  const isActive = active === step.id;
  return (
    <motion.div
      onMouseEnter={() => onEnter(step.id)}
      onMouseLeave={onLeave}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-15% 0px" }}
      transition={{ duration: 0.5, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
      data-testid={`onboarding-step-${step.id}`}
      className="group relative rounded-2xl p-4 flex flex-col transition-all"
      style={{
        background: isActive
          ? "color-mix(in srgb, var(--brand) 5%, var(--surface))"
          : "var(--surface)",
        border: `1px solid ${isActive ? "color-mix(in srgb, var(--brand) 50%, transparent)" : "var(--line-strong)"}`,
        boxShadow: isActive
          ? "0 24px 60px -30px color-mix(in srgb, var(--brand) 55%, transparent), inset 0 1px 0 color-mix(in srgb, var(--brand) 10%, transparent)"
          : "none",
      }}
    >
      {/* Compact header · number badge + icon on one line */}
      <div className="flex items-center justify-between mb-2.5">
        <div
          className="inline-flex items-center justify-center w-7 h-7 rounded-md font-display text-[13px] font-medium"
          style={{
            background: isActive ? "var(--brand)" : "color-mix(in srgb, var(--surface-2) 90%, transparent)",
            color: isActive ? "#071116" : "var(--text-2)",
          }}
        >
          {step.id}
        </div>
        <Icon size={18} strokeWidth={1.7} style={{ color: isActive ? "var(--brand)" : "var(--text-3)" }} />
      </div>

      {/* Mockup preview · shorter (110px) for a more landscape card */}
      <Mockup active={isActive} />

      {/* Title + tagline + one-line body · tightened so the card stays
          near-square rather than tall/stretched. */}
      <div className="mt-3">
        <h3 className="font-display text-[17px] font-medium text-text-1 mb-0.5 leading-tight">{step.title}</h3>
        <div className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-text-3 mb-1.5">{step.tagline}</div>
        <p className="text-text-2 text-[12.5px] leading-snug font-light">{step.body}</p>
      </div>

      {/* CTA · sits at the bottom of the card · tightened spacing */}
      <a
        href={ctaHrefOverride || step.cta.href}
        target={ctaTargetOverride !== undefined ? ctaTargetOverride : step.cta.target}
        rel={ctaRelOverride || (step.cta.target ? "noreferrer" : undefined)}
        className="mt-2.5 inline-flex items-center gap-1.5 text-[12.5px] font-medium transition-colors"
        style={{ color: isActive ? "var(--brand)" : "var(--text-2)" }}
        data-testid={`onboarding-cta-${step.id}`}
      >
        {step.cta.label}
        <ChevronRight size={13} className="group-hover:translate-x-0.5 transition-transform" />
      </a>
    </motion.div>
  );
}

export default function OnboardingSteps() {
  const { t } = useTranslation();
  const ref = useRef(null);
  const inView = useInView(ref, { margin: "-25% 0px" });
  const [active, setActive] = useState(1);
  const [hovering, setHovering] = useState(null);
  // Per-location CTA target · applies to BOTH step 1's "Start now"
  // inline link AND the master "Open account now" CTA below the grid.
  const cta = useCtaTarget("onboarding_steps");

  // i18n-driven step content · icons + CTA hrefs stay static, every
  // string flows through the active locale.
  const STEPS = useMemo(() => STEPS_META.map((m, idx) => ({
    ...m,
    title:   t(`homepage.onboarding.steps.s${idx + 1}.title`),
    tagline: t(`homepage.onboarding.steps.s${idx + 1}.tagline`),
    body:    t(`homepage.onboarding.steps.s${idx + 1}.body`),
    cta:     { ...m.cta, label: t(`homepage.onboarding.steps.s${idx + 1}.cta`) },
  })), [t]);

  // Auto-advance through steps every CYCLE_MS while the section is in
  // view and the user isn't manually hovering a card.
  useEffect(() => {
    if (!inView || hovering !== null) return;
    const id = setInterval(() => {
      setActive((a) => (a >= STEPS.length ? 1 : a + 1));
    }, CYCLE_MS);
    return () => clearInterval(id);
  }, [inView, hovering, STEPS.length]);

  // Hover lock · selecting a step pauses the auto-cycle
  const handleEnter = (id) => { setHovering(id); setActive(id); };
  const handleLeave = () => setHovering(null);

  return (
    <section
      ref={ref}
      className="py-20 lg:py-24 relative overflow-hidden"
      data-testid="onboarding-steps"
    >
      {/* Decorative grain backdrop · spans the full viewport */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.18]"
        style={{
          background: "radial-gradient(circle at 20% 30%, color-mix(in srgb, var(--brand) 14%, transparent), transparent 50%), radial-gradient(circle at 80% 70%, color-mix(in srgb, var(--brand) 9%, transparent), transparent 55%)",
        }}
      />

      {/* Inner container · mirrors the navbar exactly so its content
          edges land on the same x-coords as the logo and the
          Open Account button. The previous layout had `px-5 lg:px-8`
          on the SECTION but `max-w-7xl mx-auto` on a SEPARATE inner
          div, which made the cards 32px wider on each side than the
          navbar. */}
      <div className="max-w-7xl mx-auto px-5 lg:px-8 relative">
        {/* Section header */}
        <div className="text-center mb-12 lg:mb-14">
          <div className="pill-mono inline-flex mb-4">
            <Timer size={11} className="mr-1.5 -ml-1" /> {t("homepage.onboarding.pill", "Under 4 hours to first trade")}
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight mb-4">
            {t("homepage.onboarding.head1", "From signup to your")} <span className="text-gradient-brand font-semibold">{t("homepage.onboarding.head2", "first trade")}</span>
          </h2>
          <p className="text-[color:var(--text-2)] text-base font-light max-w-2xl mx-auto leading-relaxed">
            {t("homepage.onboarding.sub", "Three steps · open · verify & fund · trade. Each card below is a live preview of the actual flow — hover any step to see it in action.")}
          </p>
        </div>

        {/* The 3-card grid · vertical on mobile, 3-column on desktop.
            No connector line, no horizontal flow ribbon · the visual
            differentiation from LiquidityEcosystem is that each step is
            a self-contained mockup that you can preview at a glance. */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
          {STEPS.map((s, i) => (
            <StepCard
              key={s.id}
              step={s}
              index={i}
              active={active}
              onEnter={handleEnter}
              onLeave={handleLeave}
              ctaHrefOverride={s.id === 1 ? cta.href : undefined}
              ctaTargetOverride={s.id === 1 ? (cta.isExternal ? "_blank" : undefined) : undefined}
              ctaRelOverride={s.id === 1 ? (cta.isExternal ? "noopener noreferrer" : undefined) : undefined}
            />
          ))}
        </div>

        {/* Bottom strip · progress dots showing where in the cycle we
            are + master CTA. */}
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-5">
          <div className="flex items-center gap-2" data-testid="onboarding-progress">
            {STEPS.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => { setActive(s.id); setHovering(s.id); }}
                className="block rounded-full transition-all"
                style={{
                  width: active === s.id ? "28px" : "8px",
                  height: "8px",
                  background: active === s.id ? "var(--brand)" : "color-mix(in srgb, var(--brand) 25%, transparent)",
                }}
                aria-label={t("ui.onboarding.jumpToStep", { n: s.id })}
              />
            ))}
          </div>
          <a
            {...cta.linkProps}
            className="btn-solid-brand !h-12 !px-7"
            data-testid="onboarding-master-cta"
          >
            <Sparkles size={14} /> {t("ui.onboarding.masterCta")} <ArrowRight size={14} />
          </a>
        </div>

        {/* Trust strip · subtle reassurance under the CTA */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-text-3 text-[12px]">
          <span className="inline-flex items-center gap-1.5"><ShieldCheck size={13} /> {t("ui.onboarding.trustLicensed")}</span>
          <span className="inline-flex items-center gap-1.5"><Zap size={13} /> {t("ui.onboarding.trustDma")}</span>
          <span className="inline-flex items-center gap-1.5"><Timer size={13} /> {t("ui.onboarding.trustSameDay")}</span>
        </div>
      </div>
    </section>
  );
}
